import { Flex, IconButton } from '@chakra-ui/react'
import { X } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '~/components/ui/tooltip'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { hasValue } from '~/utils/common'
import { shouldPanelHandleEscape } from '~/utils/keyboardScope'

const PANEL_ANIMATION_TIME_SECONDS = 0.3

interface UserDetailsPanelProps {
  userId?: number
  onClosed?: () => void
  children: ReactNode
}

export const UserDetailsPanel = ({
  userId,
  onClosed,
  children,
}: UserDetailsPanelProps) => {
  const { t } = useTranslation()
  const [isClosing, setIsClosing] = useState<boolean>(true)

  const shouldBeClosed = !hasValue(userId)

  useEffect(() => {
    setIsClosing(shouldBeClosed)
  }, [shouldBeClosed])

  useKeyPresses({
    filter: shouldPanelHandleEscape,
    onPress: () => {
      onClosed?.()
    },
  })

  return (
    <Flex
      position="relative"
      right="0rem"
      width={isClosing ? '0rem' : '37.5rem'}
      minWidth={isClosing ? '0rem' : '22rem'}
      height="full"
      minHeight={0}
      transition={`width ${PANEL_ANIMATION_TIME_SECONDS}s`}
      tabIndex={shouldBeClosed ? -1 : 0}
      overflowX="hidden"
      bg="transparent"
    >
      {!shouldBeClosed && (
        <Tooltip
          content={t('users.detail.close', 'Close user details')}
          showArrow
          positioning={{ placement: 'bottom' }}
        >
          <IconButton
            aria-label={t('users.detail.close', 'Close user details')}
            size="sm"
            variant="subtle"
            position="absolute"
            top={0}
            left={1}
            zIndex={1}
            onClick={() => onClosed?.()}
          >
            <X />
          </IconButton>
        </Tooltip>
      )}
      <Flex
        flex="1"
        minWidth={0}
        minHeight={0}
        overflowY="auto"
        overflowX="hidden"
        paddingLeft={6}
        paddingBottom={6}
      >
        {children}
      </Flex>
    </Flex>
  )
}
